/**
 * Pure logic behind the command palette (T-0140): filtering, grouping
 * and keyboard index movement. No React, so it is unit tested directly.
 *
 * Behavioural reference: `palItems` / `renderPalette` / `onKey` in
 * `docs/requirement-library/prototype/selection.js`.
 */
import type { ReactNode } from 'react';

export interface PaletteCommand {
  /** Unique across all registered commands. */
  id: string;
  label: string;
  /** Section heading the command is listed under, for example "Go to". */
  group: string;
  /** Extra words matched by the filter but not shown. */
  keywords?: ReadonlyArray<string>;
  /** Leading icon, usually a 14px lucide glyph. */
  icon?: ReactNode;
  /** Short mono prefix, for example a requirement number. */
  mono?: string;
  /** Muted trailing hint, for example "24 requirements". */
  hint?: string;
  run: () => void;
  /** Keep the palette open after running (default closes it). */
  keepOpen?: boolean;
}

/**
 * A screen registers either a fixed list or a function of the query
 * (for search-as-you-type results such as matching records). Function
 * sources do their own matching; list sources are filtered here.
 */
export type CommandSource =
  | ReadonlyArray<PaletteCommand>
  | ((query: string) => ReadonlyArray<PaletteCommand>);

/** Lower-cased whitespace-separated terms; empty for a blank query. */
export function queryTerms(query: string): string[] {
  return query.trim().toLowerCase().split(/\s+/).filter(Boolean);
}

/** True when every term appears in the label, mono prefix or keywords. */
export function matchesCommand(command: PaletteCommand, terms: ReadonlyArray<string>): boolean {
  if (terms.length === 0) return true;
  const haystack = [command.label, command.mono ?? '', ...(command.keywords ?? [])]
    .join(' ')
    .toLowerCase();
  return terms.every((t) => haystack.includes(t));
}

/**
 * Resolve every source against the query into one flat list, ordered so
 * commands of the same group are adjacent (groups keep first-seen
 * order). Duplicate ids keep the first occurrence.
 */
export function resolveCommands(sources: Iterable<CommandSource>, query: string): PaletteCommand[] {
  const terms = queryTerms(query);
  const seen = new Set<string>();
  const byGroup = new Map<string, PaletteCommand[]>();
  for (const source of sources) {
    const list =
      typeof source === 'function' ? source(query) : source.filter((c) => matchesCommand(c, terms));
    for (const command of list) {
      if (seen.has(command.id)) continue;
      seen.add(command.id);
      const bucket = byGroup.get(command.group);
      if (bucket) bucket.push(command);
      else byGroup.set(command.group, [command]);
    }
  }
  return Array.from(byGroup.values()).flat();
}

export interface CommandGroup {
  group: string;
  /** Commands with their index in the flat list (for keyboard highlight). */
  entries: Array<{ command: PaletteCommand; index: number }>;
}

/** Split an already group-adjacent flat list into headed sections. */
export function groupCommands(commands: ReadonlyArray<PaletteCommand>): CommandGroup[] {
  const groups: CommandGroup[] = [];
  commands.forEach((command, index) => {
    const last = groups[groups.length - 1];
    if (last && last.group === command.group) last.entries.push({ command, index });
    else groups.push({ group: command.group, entries: [{ command, index }] });
  });
  return groups;
}

/** Arrow-key movement: clamps at both ends; 0 for an empty list. */
export function moveActiveIndex(current: number, delta: 1 | -1, length: number): number {
  if (length <= 0) return 0;
  return Math.min(Math.max(current + delta, 0), length - 1);
}

/** Keep the highlight valid after the result list shrinks. */
export function clampActiveIndex(current: number, length: number): number {
  return Math.min(Math.max(current, 0), Math.max(length - 1, 0));
}

/** Cmd+K on macOS, Ctrl+K elsewhere; both are accepted everywhere. */
export function isPaletteShortcut(e: {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
}): boolean {
  return Boolean(e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
}
